import {Box, Card, Container, Divider, Flex, Grid, Group, Menu, rem, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  adjustToTimeZone, DVIRBadgeSummary, DVIRSearchCriteria,
  dvirsSelector,
  getUseQueryResultData, startOfDay, subDays,
  useGetDVIRBadgeSummaryQuery,
  useGetMechanicReviewableDVIRsQuery, useSearchDVIRsQuery,
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {DVIRDataTable} from "../../components/data-table/DVIRDataTable/DVIRDataTable";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {useNavigate} from "react-router-dom";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {useAuth} from "../../providers/AuthProvider/AuthProvider";
import {createSelector} from "@reduxjs/toolkit";
import {Insight} from "../../components/insights/Insight/Insight";

export type DVIRDashboardPageProps = {}

export const DVIRDashboardPage = (props: DVIRDashboardPageProps) => {
  const {regionId} = useApp();
  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"]);

  const navigate = useNavigate()

  const selectDVIRs = useMemo(() => {
    return dvirsSelector();
  }, []);

  const reviewAccess = hasAccess("dvir_review")

  const {dvirs, isFetching, refetch} = useGetMechanicReviewableDVIRsQuery(reviewAccess && regionId ? regionId : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      dvirs: selectDVIRs(result),
      isFetching: result.isFetching,
    })
  });

  const searchArg: DVIRSearchCriteria | typeof skipToken = useMemo(() => {
    if (regionId) {
      return {
        fromDttm: adjustToTimeZone(startOfDay(subDays(new Date(), 7))).getTime(),
        region: { scope: "SPECIFIC", values: [regionId] },
      }
    } else {
      return skipToken
    }
  }, [regionId])

  const {recentDvirs, isFetchingRecent, refetch: refetchRecent} = useSearchDVIRsQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      recentDvirs: selectDVIRs(result),
      isFetchingRecent: result.isFetching,
    })
  });

  const selectDVIRBadge = useMemo(() => {
    return createSelector(
      [getUseQueryResultData<DVIRBadgeSummary>],
      (data) => {
        return data ?? {}
      }
    )
  }, []);

  const {badge, isFetchingBadge, refetch: refetchBadge} = useGetDVIRBadgeSummaryQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      badge: selectDVIRBadge(result),
      isFetchingBadge: result.isFetching,
    })
  });

  const onRefreshClick = useCallback(() => {
    if (refetch) {
      refetch()
    }
    if (refetchRecent) {
      refetchRecent()
    }
    if (refetchBadge) {
      refetchBadge()
    }
  }, [refetch, refetchRecent, refetchBadge])

  const onSearchClick = useCallback(() => {
    navigate("/dvirs/search")
  }, [navigate])

  if (!hasAccess("dvir_view")) {
    return <ErrorPage message={t<string>("common.error.unauthorized", "Unauthorized")}/>
  }

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Flex direction="column" h="100%" m="sm" gap="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title order={4}>
                      {t("common.page.entity.dvir.dashboard.title", "DVIR Dashboard")}
                    </Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={onRefreshClick}>
                          {t("common.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <Menu.Item onClick={onSearchClick}>
                          {t("common.action.search", "Search", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["dvir_create"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>

                <Box sx={{flex: 1, overflow: 'auto'}}>
                  <Grid>

                    <Grid.Col span={3}>
                      <Insight
                        loading={isFetchingBadge}
                        title={t<string>("common.page.entity.dvir.dashboard.badge.created", "Created in Past Week")}
                        icon={"chartHistogram"}
                        value={badge.createdPast7DaysCount}/>
                    </Grid.Col>

                    <Grid.Col span={3}>
                      <Insight
                        loading={isFetchingBadge}
                        title={t<string>("common.page.entity.dvir.dashboard.badge.unresolved", "Unresolved")}
                        icon={"chartHistogram"}
                        value={badge.unresolvedCount}
                        color={badge.unresolvedCount > 0 ? "red" : undefined}/>
                    </Grid.Col>

                    <Grid.Col span={3}>
                      <Insight
                        loading={isFetchingBadge}
                        title={t<string>("common.page.entity.dvir.dashboard.badge.vehicleunresolved", "Unresolved Vehicles")}
                        icon={"chartHistogram"}
                        value={badge.vehicleUnresolvedCount}
                        color={badge.vehicleUnresolvedCount > 0 ? "red" : undefined}/>
                    </Grid.Col>

                    <Grid.Col span={3}>
                      <Insight
                        loading={isFetchingBadge}
                        title={t<string>("common.page.entity.dvir.dashboard.badge.trailerunresolved", "Unresolved Trailers")}
                        icon={"chartHistogram"}
                        value={badge.trailerUnresolvedCount}
                        color={badge.trailerUnresolvedCount > 0 ? "red" : undefined}/>
                    </Grid.Col>

                    {reviewAccess &&
                      <Grid.Col span={12}>
                        <Card shadow="sm" padding="lg" radius="md" withBorder>

                          <Group position="apart" pb="sm">
                            <Title order={4}>
                              {t("common.page.entity.dvir.dashboard.section.reviewable", "Reviewable")}
                            </Title>
                          </Group>

                          <Divider/>

                          <DVIRDataTable
                            withBorder
                            records={dvirs}
                            recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                            fetching={isFetching}/>

                        </Card>
                      </Grid.Col>
                    }

                    <Grid.Col span={12}>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>

                        <Group position="apart" pb="sm">
                          <Title order={4}>
                            {t("common.page.entity.dvir.dashboard.section.recent", "Recent DVIRs")}
                          </Title>
                        </Group>

                        <Divider/>

                        <DVIRDataTable
                          withBorder
                          records={recentDvirs}
                          recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                          fetching={isFetchingRecent}/>

                      </Card>
                    </Grid.Col>
                  </Grid>
                </Box>
              </Flex>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
